import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { TenantUserApiItem } from "../lib/types";
import {
  asRecord,
  mapUsersPagination,
  unwrapUsersListBody,
} from "./usersListMappers";
import type {
  UsersListParams,
  UsersListResult,
  UsersListRole,
} from "./usersListTypes";

/** Page size of every `GET api/v1/users` list unless a caller asks for another one. */
export const USERS_LIST_DEFAULT_PAGE_SIZE = 20;

/** Wire names of the `GET api/v1/users` query params. Single place to change them. */
export const USERS_LIST_QUERY_PARAMS = {
  role: "role",
  firstName: "first_name",
  email: "email",
  status: "status",
  page: "page",
  pageSize: "page_size",
  board: "board_id",
  class: "class_id",
  section: "section_id",
  instituteId: "institute_id",
} as const;

/** Query-key discriminator: keeps these lists apart from tenant-users based lists. */
export const USERS_LIST_SOURCE = "users-api" as const;

/**
 * Builds the ONLY query sent by a users-API list. A fresh object is built so no
 * other key (and no caller-supplied role) can reach the request.
 */
export function buildUsersListQuery(
  role: UsersListRole,
  params: UsersListParams,
): Record<string, string | number> {
  const query: Record<string, string | number> = {
    [USERS_LIST_QUERY_PARAMS.role]: role,
    [USERS_LIST_QUERY_PARAMS.page]: params.page,
    [USERS_LIST_QUERY_PARAMS.pageSize]: params.pageSize,
  };

  const search = params.search?.trim();
  if (search) {
    const key = search.includes("@")
      ? USERS_LIST_QUERY_PARAMS.email
      : USERS_LIST_QUERY_PARAMS.firstName;
    query[key] = search;
  }

  for (const key of ["board", "class", "section"] as const) {
    const value = params[key]?.trim();
    if (value) query[USERS_LIST_QUERY_PARAMS[key]] = value;
  }

  const instituteId = params.institute_id?.trim();
  if (instituteId) query[USERS_LIST_QUERY_PARAMS.instituteId] = instituteId;

  const status = params.status?.trim().toLowerCase();
  if (status && status !== "all") {
    query[USERS_LIST_QUERY_PARAMS.status] = status;
  }
  return query;
}

/** The API client returns the raw body; only an explicit `success: false` is a failure. */
function assertNotFailed(body: unknown, fallbackMessage: string): void {
  const rec = asRecord(body);
  if (rec && rec.success === false) {
    throw new Error(
      typeof rec.message === "string" && rec.message
        ? rec.message
        : fallbackMessage,
    );
  }
}

/** Creates the fetcher of one role's list: request, tolerant unwrap, row mapping. */
export function createUsersListFetcher<Row>(config: {
  role: UsersListRole;
  mapRow: (raw: TenantUserApiItem, index: number) => Row;
  failedMessage: string;
}): (params: UsersListParams) => Promise<UsersListResult<Row>> {
  return async (params) => {
    const body: unknown = await ApiClient.get(
      API_ENDPOINTS.USERS.LIST,
      buildUsersListQuery(config.role, params),
    );
    assertNotFailed(body, config.failedMessage);
    const { items, pagination } = unwrapUsersListBody(body);
    const rows = items.map((item, index) =>
      config.mapRow((asRecord(item) ?? {}) as TenantUserApiItem, index),
    );
    return {
      rows,
      pagination: mapUsersPagination(pagination, {
        page: params.page,
        pageSize: params.pageSize,
        rowCount: rows.length,
      }),
    };
  };
}
